import { useQueryClient } from "@tanstack/react-query";
import { useToolScope } from "../../features/toolExperience/useToolScope";
import { useSearchParams } from "react-router-dom";
import { ToolHeader } from "../../components/ToolPage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useWordCloud } from "../../hooks/useWordCloud";
import { CrawlForm } from "../../components/WordCloud/CrawlForm";
import { SnapshotList } from "../../components/WordCloud/SnapshotList";
import { ActiveJobsPanel } from "../../components/WordCloud/ActiveJobsPanel";
import { WordCloudViewer } from "../../components/WordCloud/WordCloudViewer";
import { WordDetailSidebar } from "../../components/WordCloud/WordDetailSidebar";
import { SnapshotComparison } from "../../components/WordCloud/SnapshotComparison";
import { ExclusionFilters } from "../../components/WordCloud/ExclusionFilters";
import { dispatchTokenExpiredEvent } from "../../utils/authEvents";
import { DEFAULT_EXCLUSION_CONFIG } from "../../types/wordcloud";
import type {
  ExclusionConfig,
  TopWord,
  WordCloudSnapshot,
} from "../../types/wordcloud";
import styles from "../../components/WordCloud/WordCloud.module.css";

type ViewState = "dashboard" | "new-crawl" | "results" | "compare";

export const WordCloudPage: React.FC = () => {
  const {
    snapshots,
    activeJobs,
    isLoading,
    error,
    loadSnapshots,
    startCrawl,
    fetchSnapshotDetail,
    fetchComparison,
    exportSnapshotCsv,
    applyFilters,
    saveExclusionsForDomain,
    scopeApi,
  } = useWordCloud();

  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient(),
    scope = useToolScope();
  const snapshotParam = searchParams.get("snapshot");
  const snapshotRequest = useRef(0);
  const [linkedError, setLinkedError] = useState("");
  const [openingSnapshot, setOpeningSnapshot] = useState(false);
  const activeJobsList = Object.values(activeJobs);

  const [viewState, setViewState] = useState<ViewState>("dashboard");
  const [activeSnapshot, setActiveSnapshot] =
    useState<WordCloudSnapshot | null>(null);
  const [selectedWord, setSelectedWord] = useState<TopWord | null>(null);
  const [sizingMode, setSizingMode] = useState<"tfidf" | "count">("tfidf");
  const [filterConfig, setFilterConfig] = useState<ExclusionConfig>(
    DEFAULT_EXCLUSION_CONFIG,
  );
  const [showFilters, setShowFilters] = useState(false);

  const handleCrawlSubmit = async (data: Parameters<typeof startCrawl>[0]) => {
    await startCrawl(data);
    setViewState("dashboard");
  };

  const loadLinkedSnapshot = useCallback(
    async (id: string) => {
      const request = ++snapshotRequest.current;
      setOpeningSnapshot(true);
      setLinkedError("");
      try {
        const snapshot = await queryClient.fetchQuery({
          queryKey: [
            "wordcloud",
            "snapshot",
            scope?.userId,
            scope?.organizationId,
            id,
          ],
          queryFn: () => fetchSnapshotDetail(id),
          staleTime: 0,
          retry: false,
        });
        if (request !== snapshotRequest.current) return;
        setActiveSnapshot(snapshot);
        setFilterConfig(snapshot.exclusion_config ?? DEFAULT_EXCLUSION_CONFIG);
        setSelectedWord(null);
        setViewState("results");
      } catch {
        if (request === snapshotRequest.current)
          setLinkedError("Could not open this snapshot.");
      } finally {
        if (request === snapshotRequest.current) setOpeningSnapshot(false);
      }
    },
    [queryClient, scope?.userId, scope?.organizationId, fetchSnapshotDetail],
  );
  const handleViewSnapshot = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("snapshot", id);
    setSearchParams(params);
  };
  useEffect(() => {
    if (snapshotParam) void loadLinkedSnapshot(snapshotParam);
    else {
      setActiveSnapshot(null);
      setViewState((current) =>
        current === "results" ? "dashboard" : current,
      );
      setLinkedError("");
      setOpeningSnapshot(false);
    }
    const pendingRequest = snapshotRequest;
    return () => {
      pendingRequest.current++;
    };
  }, [snapshotParam, loadLinkedSnapshot]);

  const handleApplyFilters = async () => {
    if (!activeSnapshot) return;
    const updated = await applyFilters(
      activeSnapshot.snapshot_id,
      filterConfig,
    );
    setActiveSnapshot(updated);
    setSelectedWord(null); // the selected word may no longer exist
  };

  const renderDashboard = () => (
    <>
      <ToolHeader
        title="Word Cloud"
        description="Explore words and content patterns across your pages."
        primaryAction={{
          label: "+ New Crawl",
          onSelect: () => setViewState("new-crawl"),
        }}
      />
      {error && (
        <p role="alert">
          {error}{" "}
          <button
            type="button"
            onClick={() => {
              void loadSnapshots();
            }}
          >
            Try again
          </button>
        </p>
      )}
      <ActiveJobsPanel jobs={activeJobsList} />
      {!isLoading && !error && (
        <SnapshotList
          snapshots={snapshots}
          onView={handleViewSnapshot}
          onCompare={() => setViewState("compare")}
        />
      )}
      {isLoading && <p role="status">Loading word clouds…</p>}

      {/* Create flow is a modal overlaying the dashboard, matching every other
          "create X" flow in the app — it no longer replaces the whole page.
          Its Cancel now lives in the modal footer instead of a page header. */}
      {viewState === "new-crawl" && (
        <CrawlForm
          onSubmit={handleCrawlSubmit}
          onCancel={() => setViewState("dashboard")}
          isLoading={isLoading}
          scopeApi={scopeApi}
        />
      )}
    </>
  );

  const renderResults = () => {
    if (!activeSnapshot) return null;

    return (
      <>
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Results: {activeSnapshot.target_url}</h1>
          <div className={styles.pageActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => exportSnapshotCsv(activeSnapshot)}
              disabled={isLoading}
            >
              {isLoading ? "Exporting..." : "Export to CSV"}
            </button>
            <button
              className={styles.secondaryButton}
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete("snapshot");
                setSearchParams(params);
                setViewState("dashboard");
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        <div className={styles.cloudControls}>
          <div className={styles.formGroupCheckbox}>
            <input
              type="checkbox"
              id="sizingToggle"
              checked={sizingMode === "count"}
              onChange={(e) =>
                setSizingMode(e.target.checked ? "count" : "tfidf")
              }
            />
            <label htmlFor="sizingToggle">
              Use Raw Frequency Size (instead of TF-IDF)
            </label>
          </div>
          <div style={{ color: "var(--text-secondary)" }}>
            {/* Two distinct counters: filtering lowers the first and not the second,
                so a drop reads as filtering rather than as lost data. */}
            Words shown: {activeSnapshot.total_words.toLocaleString()}
            {typeof activeSnapshot.total_tokens === "number" && (
              <>
                {" "}
                | Words crawled: {activeSnapshot.total_tokens.toLocaleString()}
              </>
            )}{" "}
            | Pages: {activeSnapshot.page_count}
          </div>
        </div>

        <div className={styles.filtersPanel}>
          <button
            type="button"
            className={styles.textButton}
            onClick={() => setShowFilters((current) => !current)}
            aria-expanded={showFilters}
          >
            {showFilters ? "Hide filters" : "Filters"}
          </button>

          {showFilters && (
            <ExclusionFilters
              value={filterConfig}
              onChange={setFilterConfig}
              disabled={isLoading}
              onTokenExpired={dispatchTokenExpiredEvent}
              footer={
                <div className={styles.filterActions}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleApplyFilters}
                    disabled={
                      isLoading || activeSnapshot.raw_available === false
                    }
                  >
                    {isLoading ? "Applying..." : "Apply filters"}
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() =>
                      saveExclusionsForDomain(
                        activeSnapshot.target_url,
                        filterConfig,
                      )
                    }
                    disabled={isLoading}
                  >
                    Save for this domain
                  </button>
                  {activeSnapshot.raw_available === false && (
                    <span style={{ color: "var(--text-secondary)" }}>
                      This snapshot was created before filter changes were
                      supported. Re-run the crawl to change its filters.
                    </span>
                  )}
                </div>
              }
            />
          )}
        </div>

        {activeSnapshot.top_words.length === 0 && (
          <div className={styles.cloudEmpty}>
            All words are excluded by your current filters.
          </div>
        )}

        <WordCloudViewer
          words={activeSnapshot.top_words}
          sizingMode={sizingMode}
          onWordClick={setSelectedWord}
        />

        <WordDetailSidebar
          word={selectedWord}
          onClose={() => setSelectedWord(null)}
        />
      </>
    );
  };

  const renderCompare = () => (
    <>
      <div className={styles.pageHeader}>
        <h1 className={styles.title}>Compare Snapshots</h1>
        <button
          className={styles.secondaryButton}
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            params.delete("snapshot");
            setSearchParams(params);
            setViewState("dashboard");
          }}
        >
          Back to Dashboard
        </button>
      </div>
      <SnapshotComparison
        snapshots={snapshots}
        onCompare={fetchComparison}
        isLoading={isLoading}
      />
    </>
  );

  return (
    <div className={styles.wordCloudPage}>
      {linkedError && (
        <div role="alert">
          <p>{linkedError}</p>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={openingSnapshot}
            onClick={() => {
              if (snapshotParam) void loadLinkedSnapshot(snapshotParam);
            }}
          >
            Try opening again
          </button>
        </div>
      )}
      {openingSnapshot && <p role="status">Opening snapshot…</p>}
      {/* 'new-crawl' keeps the dashboard mounted underneath — the crawl form
          renders as a modal on top of it (see renderDashboard). */}
      {(viewState === "dashboard" || viewState === "new-crawl") &&
        renderDashboard()}
      {viewState === "results" && renderResults()}
      {viewState === "compare" && renderCompare()}
    </div>
  );
};
